import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { FaEdit, FaPlus, FaSave, FaTimes } from "react-icons/fa";
import PermissionNotice from "../../components/PermissionNotice";
import useAccess from "../../hooks/useAccess";

export default function SecurityRoleManager() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { hasAnyPagePermission } = useAccess();
  const canManage = hasAnyPagePermission("PermissionManagerPage", "edit");

  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [usersByRole, setUsersByRole] = useState({});
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [editName, setEditName] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const selectedRoleRef = useRef(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    [],
  );

  const applyRole = useCallback((role, sourceUsers) => {
    selectedRoleRef.current = Number(role.r_id);
    setSelectedRoleId(Number(role.r_id));
    setEditName(role.r_name);
    setMemberIds(
      (sourceUsers[role.r_id] || []).map((user) => Number(user.id)),
    );
    setIsCreating(false);
    setMessage("");
  }, []);

  const loadData = useCallback(async () => {
    const [roleResponse, memberResponse] = await Promise.all([
      axios.get(`${BASE_URL}/api/manager/security-roles`, { headers }),
      axios.get(`${BASE_URL}/api/manager/users-by-role`, { headers }),
    ]);
    const nextRoles = roleResponse.data || [];
    const nextUsersByRole = memberResponse.data?.usersByRole || {};
    setRoles(nextRoles);
    setUsersByRole(nextUsersByRole);
    setAdmins(memberResponse.data?.admins || []);

    const activeRole =
      nextRoles.find(
        (role) => Number(role.r_id) === Number(selectedRoleRef.current),
      ) || nextRoles[0];
    if (activeRole) applyRole(activeRole, nextUsersByRole);
    return { roles: nextRoles, usersByRole: nextUsersByRole };
  }, [BASE_URL, headers, applyRole]);

  useEffect(() => {
    loadData()
      .catch(() => setMessage("Unable to load roles."))
      .finally(() => setLoading(false));
  }, [loadData]);

  const openRole = (role) => applyRole(role, usersByRole);

  const beginCreate = () => {
    selectedRoleRef.current = null;
    setSelectedRoleId(null);
    setEditName("");
    setMemberIds([]);
    setIsCreating(true);
    setMessage("");
  };

  const cancelEdit = () => {
    const selectedRole = roles.find(
      (role) => Number(role.r_id) === Number(selectedRoleId),
    );
    if (selectedRole) openRole(selectedRole);
    else if (roles[0]) openRole(roles[0]);
  };

  const saveRole = async () => {
    if (!editName.trim() || !canManage) return;
    try {
      setSaving(true);
      setMessage("");

      if (isCreating) {
        const { data } = await axios.post(
          `${BASE_URL}/api/manager/security-roles`,
          { name: editName.trim() },
          { headers },
        );
        if (memberIds.length) {
          await axios.put(
            `${BASE_URL}/api/manager/security-roles/${data.r_id}`,
            { name: editName.trim(), userIds: memberIds },
            { headers },
          );
        }
        selectedRoleRef.current = Number(data.r_id);
        const refreshed = await loadData();
        const created = refreshed.roles.find(
          (role) => Number(role.r_id) === Number(data.r_id),
        );
        if (created) applyRole(created, refreshed.usersByRole);
        setMessage("Role created successfully.");
      } else {
        await axios.put(
          `${BASE_URL}/api/manager/security-roles/${selectedRoleId}`,
          { name: editName.trim(), userIds: memberIds },
          { headers },
        );
        const refreshed = await loadData();
        const updated = refreshed.roles.find(
          (role) => Number(role.r_id) === Number(selectedRoleId),
        );
        if (updated) applyRole(updated, refreshed.usersByRole);
        setMessage("Role and members updated successfully.");
      }
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = roles.find(
    (role) => Number(role.r_id) === Number(selectedRoleId),
  );
  const adminOptions = admins.map((admin) => ({
    value: Number(admin.id),
    label: admin.name,
  }));
  const selectedMembers = adminOptions.filter((option) =>
    memberIds.includes(option.value),
  );

  if (loading) return <div className="security-role-loading">Loading roles...</div>;

  return (
    <div className="security-role-manager">
      {!canManage && (
        <PermissionNotice
          message="You can view roles and members. Only Super Admin can make changes."
          compact
        />
      )}

      <div className="security-role-page-heading">
        <div>
          <h4>Role Management</h4>
          <p>Manage role names and administrator membership.</p>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn btn-success dashboard-add-button"
            onClick={beginCreate}
            disabled={saving}
          >
            <FaPlus aria-hidden="true" /> Add Role
          </button>
        )}
      </div>

      {message && (
        <div className="security-role-message" role="status">
          {message}
        </div>
      )}

      <div className="security-role-workspace">
        <aside className="security-role-sidebar">
          <div className="security-role-sidebar-heading">
            <div>
              <h5>Roles</h5>
              <span>{roles.length} available</span>
            </div>
          </div>

          <div className="security-role-list-heading" aria-hidden="true">
            <span>Role</span>
            <span>Members</span>
            <span>Action</span>
          </div>

          <div className="security-role-list">
            {roles.map((role) => {
              const members = usersByRole[role.r_id] || [];
              const active =
                !isCreating && Number(role.r_id) === Number(selectedRoleId);
              return (
                <button
                  type="button"
                  data-no-standard-button="true"
                  key={role.r_id}
                  className={`security-role-row ${active ? "security-role-row-selected" : ""}`}
                  onClick={() => openRole(role)}
                >
                  <strong className="security-role-row-name">{role.r_name}</strong>
                  <span className="security-role-row-count">{members.length}</span>
                  <span className="security-role-row-edit" aria-hidden="true">
                    <FaEdit />
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="security-role-editor">
          <div className="security-role-editor-heading">
            <div>
              <span className="security-role-eyebrow">
                {isCreating ? "New role" : canManage ? "Edit role" : "Role details"}
              </span>
              <h4>
                {isCreating ? "Create New Role" : selectedRole?.r_name || "Role"}
              </h4>
            </div>
            {!isCreating && selectedRole && (
              <span className="security-role-id">Role ID {selectedRole.r_id}</span>
            )}
          </div>

          <fieldset className="security-role-form-section">
            <legend>Role Info</legend>
            <label htmlFor="security-edit-role-name">
              Role Name <span className="text-danger">*</span>
            </label>
            <input
              id="security-edit-role-name"
              className="form-control"
              value={editName}
              onChange={(event) => setEditName(event.target.value)}
              maxLength={45}
              disabled={
                !canManage ||
                saving ||
                (!isCreating && Number(selectedRoleId) === 17)
              }
            />
            {!isCreating && Number(selectedRoleId) === 17 && (
              <small className="security-role-help">
                The Super Admin system name cannot be changed.
              </small>
            )}
          </fieldset>

          <fieldset className="security-role-form-section">
            <legend>Assigned Administrators</legend>
            <Select
              inputId="security-role-members"
              isMulti
              options={adminOptions}
              value={selectedMembers}
              onChange={(options) =>
                setMemberIds((options || []).map((option) => option.value))
              }
              isDisabled={!canManage || saving}
              classNamePrefix="security-member-select"
              placeholder="Search and select administrators..."
              closeMenuOnSelect={false}
            />
            <small className="security-role-help">
              Selected administrators receive the permissions configured for this role.
            </small>
          </fieldset>

          {canManage && (
            <div className="security-role-editor-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cancelEdit}
                disabled={saving}
              >
                <FaTimes aria-hidden="true" /> Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveRole}
                disabled={saving || !editName.trim()}
              >
                <FaSave aria-hidden="true" />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
